import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  gamificationApi,
  AdminBadge,
} from '@/api/gamification'
import { servicesApi } from '@/api/services'
import Badge from '@/components/ui/Badge'
import {
  TrophyIcon,
  StarIcon,
  UserGroupIcon,
  PuzzlePieceIcon,
  FlagIcon,
  ChartBarIcon,
  GiftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  FireIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  SparklesIcon,
  HeartIcon,
  BoltIcon,
  ShieldCheckIcon,
  BookOpenIcon,
  DocumentCheckIcon,
  HandThumbUpIcon,
  ClockIcon,
  CalendarDaysIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'

// Mapping from icon name to HeroIcon component
const BadgeIconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  'academic-cap': AcademicCapIcon,
  'star': StarIcon,
  'chat': ChatBubbleLeftRightIcon,
  'trophy': TrophyIcon,
  'fire': FireIcon,
  'lightbulb': LightBulbIcon,
  'rocket': RocketLaunchIcon,
  'sparkles': SparklesIcon,
  'heart': HeartIcon,
  'bolt': BoltIcon,
  'shield': ShieldCheckIcon,
  'book': BookOpenIcon,
  'document': DocumentCheckIcon,
  'thumbup': HandThumbUpIcon,
  'clock': ClockIcon,
  'calendar': CalendarDaysIcon,
  'users': UsersIcon,
  'flag': FlagIcon,
  'puzzle': PuzzlePieceIcon,
  'gift': GiftIcon,
}

// Badge icon colors based on icon type
const iconColors: Record<string, string> = {
  'academic-cap': 'text-blue-600',
  'star': 'text-yellow-500',
  'chat': 'text-green-600',
  'trophy': 'text-yellow-600',
  'fire': 'text-orange-500',
  'lightbulb': 'text-amber-500',
  'rocket': 'text-purple-600',
  'sparkles': 'text-pink-500',
  'heart': 'text-red-500',
  'bolt': 'text-yellow-400',
  'shield': 'text-emerald-600',
  'book': 'text-indigo-600',
  'document': 'text-slate-600',
  'thumbup': 'text-blue-500',
  'clock': 'text-gray-600',
  'calendar': 'text-teal-600',
  'users': 'text-violet-600',
  'flag': 'text-red-600',
  'puzzle': 'text-cyan-600',
  'gift': 'text-pink-600',
}

// Extract icon name from URL or use directly
const getIconName = (icon?: string): string => {
  if (!icon) return ''
  // If it's a URL, extract the last segment (icon name)
  if (icon.startsWith('http')) {
    const parts = icon.split('/')
    return parts[parts.length - 1] || ''
  }
  return icon
}

// Badge icon component
const BadgeIcon = ({ icon, className = "w-10 h-10" }: { icon?: string; className?: string }) => {
  const iconName = getIconName(icon)

  if (!iconName) {
    return <TrophyIcon className={`${className} text-yellow-500`} />
  }

  // Get the HeroIcon component
  const iconKey = iconName.toLowerCase()
  const IconComponent = BadgeIconMap[iconKey]
  const colorClass = iconColors[iconKey] || 'text-primary-600'

  if (IconComponent) {
    return <IconComponent className={`${className} ${colorClass}`} />
  }

  // Fallback to trophy
  return <TrophyIcon className={`${className} text-yellow-500`} />
}

export default function AdminGamificationPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'overview' | 'badges' | 'users' | 'activities'>('overview')
  const [showAwardModal, setShowAwardModal] = useState(false)
  const [selectedBadge, setSelectedBadge] = useState<AdminBadge | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [awardReason, setAwardReason] = useState('')

  // Queries
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['admin-gamification-stats'],
    queryFn: gamificationApi.getAdminStats,
  })

  const { data: badges, isLoading: loadingBadges } = useQuery({
    queryKey: ['admin-badges'],
    queryFn: () => gamificationApi.getAdminBadges(),
  })

  const { data: userLevels, isLoading: loadingUsers } = useQuery({
    queryKey: ['admin-user-levels'],
    queryFn: () => gamificationApi.getAdminUserLevels(),
  })

  const { data: escapeRooms } = useQuery({
    queryKey: ['admin-escape-rooms'],
    queryFn: () => gamificationApi.getAdminEscapeRooms(),
  })

  const { data: redFlagHunts } = useQuery({
    queryKey: ['admin-red-flag-hunts'],
    queryFn: () => gamificationApi.getAdminRedFlagHunts(),
  })

  const { data: users } = useQuery({
    queryKey: ['admin-users-list'],
    queryFn: () => servicesApi.getAvailableExperts(),
  })

  // Mutations
  const awardBadgeMutation = useMutation({
    mutationFn: ({ slug, userId, reason }: { slug: string; userId: number; reason: string }) =>
      gamificationApi.awardBadgeToUser(slug, userId, reason),
    onSuccess: (data) => {
      toast.success(data.detail)
      queryClient.invalidateQueries({ queryKey: ['admin-badges'] })
      queryClient.invalidateQueries({ queryKey: ['admin-user-levels'] })
      setShowAwardModal(false)
      setSelectedBadge(null)
      setSelectedUserId(null)
      setAwardReason('')
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      toast.error(error.response?.data?.error || 'Errore nell\'assegnazione del badge')
    },
  })

  const handleAwardBadge = () => {
    if (selectedBadge && selectedUserId) {
      awardBadgeMutation.mutate({
        slug: selectedBadge.slug || selectedBadge.code,
        userId: selectedUserId,
        reason: awardReason || 'Assegnato manualmente da admin',
      })
    }
  }

  // Stats Cards
  const statCards = [
    {
      title: 'Punti Totali',
      value: stats?.total_points_awarded?.toLocaleString() || '0',
      icon: StarIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      title: 'Badge Assegnati',
      value: stats?.total_badges_awarded || 0,
      icon: TrophyIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Utenti Attivi',
      value: stats?.active_users_count || 0,
      icon: UserGroupIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Livello Medio',
      value: stats?.avg_user_level?.toFixed(1) || '1.0',
      icon: ChartBarIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Escape Room Completate',
      value: stats?.escape_rooms_completed || 0,
      icon: PuzzlePieceIcon,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
    },
    {
      title: 'Red Flag Completate',
      value: stats?.red_flag_hunts_completed || 0,
      icon: FlagIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
    },
  ]

  const actionLabels: Record<string, string> = {
    course_complete: 'Corso completato',
    lesson_complete: 'Lezione completata',
    quiz_pass: 'Quiz superato',
    thread_create: 'Thread creato',
    post_create: 'Post creato',
    post_solution: 'Soluzione accettata',
    escape_room: 'Escape Room',
    red_flag: 'Red Flag Hunt',
    event_attend: 'Evento frequentato',
    document_upload: 'Documento caricato',
    daily_login: 'Login giornaliero',
    badge_earned: 'Badge ottenuto',
    manual: 'Manuale',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Gamification</h1>
          <p className="text-gray-600">Gestisci badge, punti e attività di gamification</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/gestione/tornei"
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <TrophyIcon className="h-5 w-5" />
            Gestione Tornei
          </Link>
          <button
            onClick={() => refetchStats()}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((card) => (
          <div key={card.title} className="bg-white rounded-lg shadow-sm p-4">
            <div className={`inline-flex p-2 rounded-lg ${card.bgColor} mb-3`}>
              <card.icon className={`h-5 w-5 ${card.color}`} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{card.value}</p>
            <p className="text-sm text-gray-500">{card.title}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Panoramica' },
            { id: 'badges', label: 'Badge' },
            { id: 'users', label: 'Utenti e Livelli' },
            { id: 'activities', label: 'Attivita' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Points by Action */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Distribuzione Punti per Azione</h3>
            {stats?.points_by_action && Object.keys(stats.points_by_action).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.points_by_action)
                  .sort((a, b) => b[1].total_points - a[1].total_points)
                  .slice(0, 8)
                  .map(([action, data]) => (
                    <div key={action} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {actionLabels[action] || action}
                      </span>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-400">{data.count} volte</span>
                        <span className="font-medium text-gray-900">
                          {data.total_points.toLocaleString()} pts
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nessun dato disponibile</p>
            )}
          </div>

          {/* Top Users */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Top 10 Utenti</h3>
            {loadingUsers ? (
              <p className="text-gray-500 text-center py-8">Caricamento...</p>
            ) : userLevels && userLevels.length > 0 ? (
              <div className="space-y-3">
                {userLevels.slice(0, 10).map((user, index) => (
                  <div key={user.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-100 text-gray-700' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{user.user_name}</p>
                        <p className="text-xs text-gray-500">Livello {user.level}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">{user.total_points.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">{user.badges_count} badge</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nessun utente trovato</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'badges' && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold">Gestione Badge</h3>
            <button
              onClick={() => setShowAwardModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <GiftIcon className="h-5 w-5" />
              Assegna Badge
            </button>
          </div>
          {loadingBadges ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Badge</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Punti</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assegnati</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {badges?.map((badge) => (
                    <tr key={badge.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                            <BadgeIcon icon={badge.icon} className="w-6 h-6" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{badge.name}</p>
                            <p className="text-sm text-gray-500">{badge.description?.slice(0, 50)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={badge.type === 'achievement' ? 'primary' : badge.type === 'special' ? 'warning' : 'default'}>
                          {badge.type}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {badge.points_value} pts
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {badge.users_count} utenti
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {badge.is_active ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircleIcon className="h-4 w-4" /> Attivo
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-400">
                            <XCircleIcon className="h-4 w-4" /> Inattivo
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setSelectedBadge(badge)
                            setShowAwardModal(true)
                          }}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        >
                          Assegna
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Livelli Utenti</h3>
          </div>
          {loadingUsers ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Livello</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Punti Totali</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Punti Correnti</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Badge</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userLevels?.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="font-medium text-gray-900">{user.user_name}</p>
                          <p className="text-sm text-gray-500">{user.user_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="primary">Livello {user.level}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.total_points.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.current_points.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.badges_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'activities' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Escape Rooms */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <PuzzlePieceIcon className="h-5 w-5 text-indigo-600" />
                Escape Room
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {escapeRooms?.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Nessuna Escape Room configurata
                </div>
              ) : (
                escapeRooms?.map((room) => (
                  <div key={room.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{room.title}</h4>
                        <p className="text-sm text-gray-500">
                          {room.stages_count} stage &bull; {room.time_limit_minutes} min &bull; {room.points_reward} pts
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant={room.is_active ? 'success' : 'default'}>
                          {room.is_active ? 'Attiva' : 'Inattiva'}
                        </Badge>
                        <p className="text-sm text-gray-500 mt-1">
                          {room.completions_count} completamenti
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Red Flag Hunts */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FlagIcon className="h-5 w-5 text-red-600" />
                Red Flag Hunt
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {redFlagHunts?.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Nessuna Red Flag Hunt configurata
                </div>
              ) : (
                redFlagHunts?.map((hunt) => (
                  <div key={hunt.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{hunt.title}</h4>
                        <p className="text-sm text-gray-500">
                          {hunt.flags_count} flag &bull; {hunt.points_per_flag} pts/flag
                          {hunt.time_limit_minutes && ` &bull; ${hunt.time_limit_minutes} min`}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant={hunt.is_active ? 'success' : 'default'}>
                          {hunt.is_active ? 'Attiva' : 'Inattiva'}
                        </Badge>
                        <p className="text-sm text-gray-500 mt-1">
                          {hunt.completions_count} completamenti
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Award Badge Modal */}
      {showAwardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Assegna Badge</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Badge</label>
                <select
                  value={selectedBadge?.id || ''}
                  onChange={(e) => {
                    const badge = badges?.find((b) => b.id === Number(e.target.value))
                    setSelectedBadge(badge || null)
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Seleziona un badge</option>
                  {badges?.map((badge) => (
                    <option key={badge.id} value={badge.id}>
                      🏆 {badge.name} ({badge.points_value} pts)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utente</label>
                <select
                  value={selectedUserId || ''}
                  onChange={(e) => setSelectedUserId(Number(e.target.value) || null)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Seleziona un utente</option>
                  {users?.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivazione</label>
                <input
                  type="text"
                  value={awardReason}
                  onChange={(e) => setAwardReason(e.target.value)}
                  placeholder="Motivazione assegnazione..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAwardModal(false)
                  setSelectedBadge(null)
                  setSelectedUserId(null)
                  setAwardReason('')
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleAwardBadge}
                disabled={!selectedBadge || !selectedUserId || awardBadgeMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {awardBadgeMutation.isPending ? 'Assegnazione...' : 'Assegna Badge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
